import type { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import {
    UpgradeSubscriptionPlanAPICall,
    UpgradeSubscriptionSetAPICall,
} from 'reactApp/api/billing/upgradeSubscription/UpgradeSubscriptionAPICall';
import type {
    UpgradeSubscriptionPlanArgs,
    UpgradeSubscriptionSetArgs,
} from 'reactApp/api/billing/upgradeSubscription/UpgradeSubscriptionAPICall.types';
import { renderNewComfirmationDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { getBuyApiParams } from 'reactApp/modules/billing/billing.helpers';
import { productsController } from 'reactApp/modules/products/products.controller';
import type {
    UpgradeSubscriptionPlanAction,
    UpgradeSubscriptionSetAction,
} from 'reactApp/modules/upgradeSubscription/upgradeSubscription.types';
import { sendXray } from 'reactApp/utils/ga';
import { cancel, put, select, takeEvery } from 'redux-saga/effects';
import { call } from 'typed-redux-saga';

import { getCurrentStorage } from '../router/router.selectors';
import { EStorageType } from '../storage/storage.types';
import {
    upgradeSubscriptionPlanRequest,
    upgradeSubscriptionPlanSuccess,
    upgradeSubscriptionSetRequest,
    upgradeSubscriptionSetSuccess,
} from './upgradeSubscription.module';

const notSuccessUpgrade = () => {
    renderNewComfirmationDialog({
        dataQAId: 'no-tariff-dialog',
        renderHeader: () => 'Этот тариф для вас недоступен',
        renderContent: () => 'Смените аккаунт и\u00A0попробуйте\u00A0ещё\u00A0раз',
        buttons: [EButtonType.cancel],
        cancelButtonText: 'Закрыть',
    });
};

const upgradePlanApiCall = (params: UpgradeSubscriptionPlanArgs) => new UpgradeSubscriptionPlanAPICall().makeRequest(params);
const upgradeSetApiCall = (params: UpgradeSubscriptionSetArgs) => new UpgradeSubscriptionSetAPICall().makeRequest(params);

function* upgradeSubscriptionPlanSaga(action: PayloadAction<UpgradeSubscriptionPlanAction>) {
    const { sub_id, product_id, isMobile, isMobilePayAvailable } = action.payload;

    sendXray(['upgrade', 'request', 'start']);

    try {
        yield* call(productsController.loadProducts);
        const storage = yield select(getCurrentStorage);

        if (isMobile && !isMobilePayAvailable) {
            return;
        }

        const { data: upgradePlan } = yield* call(upgradePlanApiCall, { sub_id, product_id });

        if (upgradePlan?.result !== 'success' && (storage === EStorageType.quotaCleaner || storage === EStorageType.quotaTariffs)) {
            notSuccessUpgrade();
            yield cancel();
        } else {
            yield put(upgradeSubscriptionPlanSuccess(upgradePlan));

            yield put(
                upgradeSubscriptionSetRequest({
                    ptime: Date.now(),
                    sub_id,
                    product_id,
                    isMobile,
                })
            );
        }
    } catch (error) {
        sendXray(['upgrade', 'plan', 'fail']);
        logger.error(error);
    }
}

function* upgradeSubscriptionSetSaga(action: PayloadAction<UpgradeSubscriptionSetAction>) {
    const { ptime, sub_id, product_id, isMobile } = action.payload;

    const buyParams = getBuyApiParams({ isMidas: true, id: product_id, isMobile });

    try {
        sendXray(['upgrade', 'set', 'start']);
        const storage = yield select(getCurrentStorage);
        const { data: upgradeSet } = yield* call(upgradeSetApiCall, { ptime, sub_id, product_id, ...buyParams });
        if (upgradeSet && upgradeSet?.result === 'success' && upgradeSet?.type === 'payment' && upgradeSet?.pw) {
            yield put(upgradeSubscriptionSetSuccess(upgradeSet));
            sendXray(['upgrade', 'set', 'result', 'success']);
        } else if (upgradeSet?.result === 'fail' && (storage === EStorageType.quotaCleaner || storage === EStorageType.quotaTariffs)) {
            sendXray(['upgrade', 'set', 'result', 'fail']);
            notSuccessUpgrade();
            yield cancel();
        }
    } catch (error) {
        sendXray(['upgrade', 'set', 'fail']);
        logger.error(error);
    }
}

export function* watchUpgradeSubscriptionPlan() {
    yield takeEvery(upgradeSubscriptionPlanRequest.toString(), upgradeSubscriptionPlanSaga);
    yield takeEvery(upgradeSubscriptionSetRequest.toString(), upgradeSubscriptionSetSaga);
}
